import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getProfile, getStoredUser, login as loginRequest, logout as logoutService } from "../services/authService";
import { getAuthToken } from "../services/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const bootAuth = async () => {
      try {
        const savedToken = getAuthToken();
        const savedUser = getStoredUser();

        if (savedToken) setToken(savedToken);
        if (savedUser) setUser(savedUser);

        if (savedToken) {
          const profile = await getProfile().catch(() => null);
          const resolvedUser = profile?.user || profile?.profile || profile?.data || profile || savedUser || null;
          if (resolvedUser) {
            setUser(resolvedUser);
          }
        }
      } catch (error) {
        console.error("Gagal boot auth:", error);
        await logoutService();
        setToken(null);
        setUser(null);
      } finally {
        setAuthReady(true);
      }
    };

    bootAuth();
  }, []);

  const login = useCallback(async ({ email, username, password }) => {
    const data = await loginRequest({ email, username, password });
    const resolvedToken = data?.access || data?.token || data?.access_token || getAuthToken() || "";
    const resolvedUser = data?.user || data?.profile || data?.data?.user || getStoredUser() || null;

    setToken(resolvedToken || null);
    setUser(resolvedUser || null);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await logoutService();
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((nextUser) => {
    setUser((prev) => ({ ...(prev || {}), ...(nextUser || {}) }));
  }, []);

  const isAuthenticated = Boolean(token);

  const value = useMemo(
    () => ({ authReady, token, user, isAuthenticated, login, logout, updateUser, setToken, setUser }),
    [authReady, token, user, isAuthenticated, login, logout, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth harus digunakan di dalam AuthProvider");
  }
  return context;
}
